import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, Zap, PhoneOff, Copy, CheckCircle2, Settings, ChevronRight } from 'lucide-react';
import { Button } from '../ui/Button';
import { useTheme } from '../../contexts/ThemeContext';
type ForwardTarget = 'vexu' | 'other' | 'disabled';
interface ForwardingScenario {
  id: string;
  name: string;
  icon: React.ElementType;
  status: ForwardTarget;
  otherNumber?: string;
}
interface ActivationOverlaysProps {
  syncPhase: 'idle' | 'confirm' | 'scanning' | 'synced';
  scanProgress: number;
  scanText: string;
  confirmSync: () => void;
  setSyncPhase: (phase: 'idle' | 'confirm' | 'scanning' | 'synced') => void;
  switchPhase: 'confirm' | 'activating' | 'done' | null;
  switchTarget: ForwardingScenario | null;
  confirmSwitch: () => void;
  setSwitchPhase: (phase: 'confirm' | 'activating' | 'done' | null) => void;
  setSwitchTarget: (target: ForwardingScenario | null) => void;
  disablePhase: 'confirm' | 'disabling' | 'done' | null;
  disableTarget: ForwardingScenario | null;
  confirmDisable: () => void;
  setDisablePhase: (phase: 'confirm' | 'disabling' | 'done' | null) => void;
  setDisableTarget: (target: ForwardingScenario | null) => void;
  VEXU_NUMBER: string;
}
export function ActivationOverlays({
  syncPhase,
  scanProgress,
  scanText,
  confirmSync,
  setSyncPhase,
  switchPhase,
  switchTarget,
  confirmSwitch,
  setSwitchPhase,
  setSwitchTarget,
  disablePhase,
  disableTarget,
  confirmDisable,
  setDisablePhase,
  setDisableTarget,
  VEXU_NUMBER
}: ActivationOverlaysProps) {
  const {
    isLight
  } = useTheme();
  const UssdConfirmModal = ({
    title,
    description,
    onConfirm,
    onCancel
  }: {
    title: string;
    description: string;
    onConfirm: () => void;
    onCancel: () => void;
  }) => <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-188" data-mp="12fjy4zny4hve">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} data-id="element-189" data-mp="2rwfdmglko7g9" />
      <motion.div initial={{
      y: 200,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: 200,
      opacity: 0
    }} transition={{
      type: 'spring',
      stiffness: 400,
      damping: 30
    }} className={`relative w-full max-w-sm rounded-3xl overflow-hidden ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-190" data-mp="2wq9xpl53aofv">
        <div className="p-6" data-id="element-191" data-mp="30seng8sf30x8">
          <h3 className={`text-lg font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-192" data-mp="35oluy60gokha">
            {title}
          </h3>
          <p className={`text-sm mb-6 leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-193" data-mp="1pczt7crlen5t">
            {description}
          </p>
          <div className="space-y-3" data-id="element-194" data-mp="cb1zba96fe2q">
            <Button onClick={onConfirm} fullWidth data-id="element-195" data-mp="ce6batsvve3y">
              Continue
            </Button>
            <button onClick={onCancel} className={`w-full py-3 text-sm font-medium transition-colors ${isLight ? 'text-slate-400 hover:text-slate-600' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-196" data-mp="7fcvguq4jb0m">
              Cancel
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>;
  const IosManualModal = ({
    title,
    ussdCode,
    phoneNumber,
    onConfirm,
    onCancel
  }: {
    title: string;
    ussdCode: string;
    phoneNumber: string;
    onConfirm: () => void;
    onCancel: () => void;
  }) => {
    const [copiedUssd, setCopiedUssd] = useState(false);
    const [copiedPhone, setCopiedPhone] = useState(false);
    const copyToClipboard = (text: string, isUssd: boolean) => {
      navigator.clipboard.writeText(text);
      if (isUssd) {
        setCopiedUssd(true);
        setTimeout(() => setCopiedUssd(false), 2000);
      } else {
        setCopiedPhone(true);
        setTimeout(() => setCopiedPhone(false), 2000);
      }
    };
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-197" data-mp="3op2fc2obfbti">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} data-id="element-198" data-mp="28y8clwl6nrt" />
        <motion.div initial={{
        y: 200,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 200,
        opacity: 0
      }} transition={{
        type: 'spring',
        stiffness: 400,
        damping: 30
      }} className={`relative w-full max-w-sm rounded-3xl overflow-hidden ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-199" data-mp="6d606ryccju9">
          <div className="p-6" data-id="element-200" data-mp="25puww5kmsr38">
            <div className="flex items-center gap-2 mb-2" data-id="element-201" data-mp="25otips8bjoha">
              <Settings className={`w-5 h-5 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-202" data-mp="20qryh3z5pj48" />
              <h3 className={`text-lg font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-203" data-mp="1vt9nl81kwttl">
                {title}
              </h3>
            </div>

            <p className={`text-sm mb-5 leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-204" data-mp="2ogmc3nravbpj">
              {phoneNumber ? "You'll set this up directly in your phone's Settings — just follow the steps below." : "Turn this off directly in your phone's Settings using the steps below."}
            </p>

            {/* Settings path pill */}
            <div className={`flex items-center gap-1.5 p-3 rounded-xl mb-4 ${isLight ? 'bg-slate-50 border border-slate-200' : 'bg-white/5 border border-white/10'}`} data-id="element-205" data-mp="132rn8es8p6jv">
              <Settings className={`w-4 h-4 flex-shrink-0 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-206" data-mp="3va207w5d5ulj" />
              <span className={`text-[13px] font-medium flex items-center gap-1 ${isLight ? 'text-slate-700' : 'text-zinc-200'}`} data-id="element-207" data-mp="3hdepmqkhn5x5">
                Settings
                <ChevronRight className="w-3.5 h-3.5 opacity-50" data-id="element-208" data-mp="37k7ojhws6c2v" />
                Phone
                <ChevronRight className="w-3.5 h-3.5 opacity-50" data-id="element-209" data-mp="3cdsc0g0qznnt" />
                Call Forwarding
              </span>
            </div>

            {/* Step-by-step guide */}
            <ol className="space-y-3 mb-6" data-id="element-210" data-mp="33kyv6e5nkg7u">
              {(phoneNumber ? ['Open the Settings app, then tap Phone › Call Forwarding.', 'Turn Call Forwarding on.', 'Tap “Forward To” and enter the Vexu number below.'] : ['Open the Settings app, then tap Phone › Call Forwarding.', 'Turn Call Forwarding off.']).map((stepText, i) => <li key={i} className="flex items-start gap-3" data-id="element-211" data-mp="2jzosq54rl59b" data-mpi={i}>
                  <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-212" data-mp="2eyrdo1f1wrc1">
                    {i + 1}
                  </span>
                  <span className={`text-[13px] leading-relaxed pt-0.5 ${isLight ? 'text-slate-600' : 'text-zinc-300'}`} data-id="element-213" data-mp="32p4x53jklhv0">
                    {stepText}
                  </span>
                </li>)}
            </ol>

            {/* Vexu number to forward to (enabling only) */}
            {phoneNumber && <div className="mb-6" data-id="element-214" data-mp="37lln83aby6aq">
                <label className={`text-xs font-bold uppercase tracking-wider mb-1.5 block ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-215" data-mp="1sbvpkk1fm52d">
                  Vexu Forwarding Number
                </label>
                <div className={`flex items-center justify-between p-3 rounded-xl border ${isLight ? 'bg-slate-50 border-slate-200' : 'bg-white/5 border-white/10'}`} data-id="element-216" data-mp="22a35czjzih2s">
                  <span className={`text-sm font-semibold font-mono ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-217" data-mp="19mpe9i2ywfql">
                    {phoneNumber}
                  </span>
                  <button onClick={() => copyToClipboard(phoneNumber, false)} className={`p-2 rounded-lg transition-colors ${copiedPhone ? 'bg-vexu/10 text-vexu' : isLight ? 'bg-white border border-slate-200 text-slate-500 hover:bg-slate-50' : 'bg-white/10 text-zinc-400 hover:bg-white/20'}`} data-id="element-218" data-mp="1jgz6mzrwte1b">
                    {copiedPhone ? <CheckCircle2 className="w-4 h-4" data-id="element-219" data-mp="391n22gsd1496" /> : <Copy className="w-4 h-4" data-id="element-220" data-mp="352nns5kqnv52" />}
                  </button>
                </div>
              </div>}

            <div className="space-y-3" data-id="element-221" data-mp="2v485ms3ovw7p">
              <Button onClick={onConfirm} fullWidth data-id="element-222" data-mp="2v3gbzmtnk8k2">
                {phoneNumber ? "I've enabled it" : "I've disabled it"}
              </Button>
              <button onClick={onCancel} className={`w-full py-3 text-sm font-medium transition-colors ${isLight ? 'text-slate-400 hover:text-slate-600' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-223" data-mp="2le6gry3ii9rq">
                Cancel
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>;
  };
  const ScanningOverlay = () => <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.4,
    ease: 'easeInOut'
  }} className="absolute inset-0 z-50 flex flex-col items-center justify-center p-8" data-id="element-224" data-mp="22fatz7tzgxnx">
      <div className={`absolute inset-0 ${isLight ? 'bg-white/95' : 'bg-[#0A0A0A]/95'} backdrop-blur-xl`} data-id="element-225" data-mp="uhcqw21spcoj" />
      <div className="relative z-10 flex flex-col items-center text-center w-full max-w-[280px]" data-id="element-226" data-mp="qgsu4zl4wb2x">
        <div className="relative w-32 h-32 mb-8" data-id="element-227" data-mp="2qq6cvo95ra4">
          <motion.div animate={{
          scale: [1, 1.25, 1],
          opacity: [0.25, 0.08, 0.25]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute inset-0 rounded-full border-2 border-vexu/30" data-id="element-228" data-mp="3tw84y31c6op4" />
          <motion.div animate={{
          rotate: 360
        }} transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'linear'
        }} className="absolute inset-4 rounded-full border-2 border-transparent border-t-vexu border-r-vexu/30" data-id="element-229" data-mp="1n3xhv1eczmrk" />
          <div className="absolute inset-0 flex items-center justify-center" data-id="element-230" data-mp="1gprwrbhr8nhs">
            <motion.div animate={{
            scale: [1, 1.08, 1]
          }} transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-231" data-mp="1gruwwocmfx08">
              <RefreshCw className="w-10 h-10 text-vexu" data-id="element-232" data-mp="2ebz4gu9cuvyd" />
            </motion.div>
          </div>
        </div>
        <p className={`text-base font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-233" data-mp="1znuqg3l8z8lx">
          Checking Call Forwarding Settings
        </p>
        <div className="h-5 relative w-full flex justify-center mb-6" data-id="element-234" data-mp="2sc0diwzrx9ox">
          <AnimatePresence mode="wait" data-id="element-235" data-mp="2x3i6oyvk25zc">
            <motion.p key={scanText} initial={{
            opacity: 0,
            y: 8,
            filter: 'blur(4px)'
          }} animate={{
            opacity: 1,
            y: 0,
            filter: 'blur(0px)'
          }} exit={{
            opacity: 0,
            y: -8,
            filter: 'blur(4px)'
          }} transition={{
            duration: 0.6,
            ease: 'easeInOut'
          }} className={`text-sm absolute ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-236" data-mp="2x5cq28r6hq1b">
              {scanText}
            </motion.p>
          </AnimatePresence>
        </div>
        <div className="w-full max-w-[200px]" data-id="element-237" data-mp="29c04e64tic0h">
          <div className={`h-1.5 rounded-full overflow-hidden ${isLight ? 'bg-slate-100' : 'bg-white/5'}`} data-id="element-238" data-mp="29db5tzasj7of">
            <motion.div className="h-full rounded-full bg-vexu" initial={{
            width: '0%'
          }} animate={{
            width: `${scanProgress}%`
          }} transition={{
            duration: 0.8,
            ease: [0.25, 0.46, 0.45, 0.94]
          }} data-id="element-239" data-mp="25chgr2lm3mty" />
          </div>
        </div>
      </div>
    </motion.div>;
  const ActivationOverlay = () => <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="absolute inset-0 z-50 flex flex-col items-center justify-center p-8" data-id="element-240" data-mp="trr4aygr0la9">
      <div className={`absolute inset-0 ${isLight ? 'bg-white/95' : 'bg-[#0A0A0A]/95'} backdrop-blur-xl`} data-id="element-241" data-mp="120psob7fhdm" />
      <div className="relative z-10 flex flex-col items-center text-center w-full max-w-[280px]" data-id="element-242" data-mp="aupvt3nzk31k">
        <div className="relative w-32 h-32 mb-8" data-id="element-243" data-mp="39kq2alxtp7gh">
          <motion.div animate={{
          scale: [1, 1.3, 1],
          opacity: [0.3, 0.1, 0.3]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute inset-0 rounded-full border-2 border-vexu/30" data-id="element-244" data-mp="39jwrn9vqtxt2" />
          <motion.div animate={{
          rotate: 360
        }} transition={{
          duration: 2.5,
          repeat: Infinity,
          ease: 'linear'
        }} className="absolute inset-4 rounded-full border-2 border-transparent border-t-vexu border-r-vexu/30" data-id="element-245" data-mp="1tt4am8o0cedx" />
          <div className="absolute inset-0 flex items-center justify-center" data-id="element-246" data-mp="1yrpdygyk3e6l">
            <motion.div animate={{
            scale: [1, 1.1, 1]
          }} transition={{
            duration: 1.5,
            repeat: Infinity
          }} data-id="element-247" data-mp="2cqfb1wldlduy">
              <Zap className="w-10 h-10 text-vexu" data-id="element-248" data-mp="ifs3nowybfnj" />
            </motion.div>
          </div>
        </div>
        <p className={`text-base font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-249" data-mp="eeglg4gepwpd">
          Activating Vexu.ai
        </p>
        <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-250" data-mp="88h3d40gkdqb">
          Configuring forwarding for "{switchTarget?.name}"...
        </p>
      </div>
    </motion.div>;
  const DisableOverlay = () => <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="absolute inset-0 z-50 flex flex-col items-center justify-center p-8" data-id="element-251" data-mp="3vki24dmn0gk0">
      <div className={`absolute inset-0 ${isLight ? 'bg-white/95' : 'bg-[#0A0A0A]/95'} backdrop-blur-xl`} data-id="element-252" data-mp="37pnta6f95yiy" />
      <div className="relative z-10 flex flex-col items-center text-center w-full max-w-[280px]" data-id="element-253" data-mp="3coik9vndite5">
        <div className="relative w-32 h-32 mb-8" data-id="element-254" data-mp="15y2abm5fefa4">
          <motion.div animate={{
          scale: [1, 1.3, 1],
          opacity: [0.3, 0.1, 0.3]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute inset-0 rounded-full border-2 border-red-500/30" data-id="element-255" data-mp="1awc7216stnxu" />
          <motion.div animate={{
          rotate: 360
        }} transition={{
          duration: 2.5,
          repeat: Infinity,
          ease: 'linear'
        }} className="absolute inset-4 rounded-full border-2 border-transparent border-t-red-500 border-r-red-500/30" data-id="element-256" data-mp="zhiyfzl6cpyl" />
          <div className="absolute inset-0 flex items-center justify-center" data-id="element-257" data-mp="1iflehovqvck9">
            <motion.div animate={{
            scale: [1, 1.1, 1]
          }} transition={{
            duration: 1.5,
            repeat: Infinity
          }} data-id="element-258" data-mp="1dh1qzk1i5sps">
              <PhoneOff className="w-10 h-10 text-red-500" data-id="element-259" data-mp="265fqy5c5iz3z" />
            </motion.div>
          </div>
        </div>
        <p className={`text-base font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-260" data-mp="2dhx64c0yhxse">
          Disabling Vexu.ai
        </p>
        <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-261" data-mp="2nf1k3oknemy4">
          Removing forwarding for "{disableTarget?.name}"...
        </p>
      </div>
    </motion.div>;
  return <>
      <AnimatePresence data-id="element-262" data-mp="2wghvnzn78v3x">
        {syncPhase === 'confirm' && <UssdConfirmModal title="Set up on your phone" description="Call forwarding is managed in your phone's Settings. Continue and we'll check your current forwarding, then guide you through Settings for each scenario." onConfirm={confirmSync} onCancel={() => setSyncPhase('idle')} data-id="element-263" data-mp="2wdvkzr50hzhh" />}
      </AnimatePresence>

      <AnimatePresence data-id="element-264" data-mp="3g5jt1681zijg">
        {syncPhase === 'scanning' && <ScanningOverlay data-id="element-265" data-mp="3k4iziv9fnnd0" />}
      </AnimatePresence>

      <AnimatePresence data-id="element-266" data-mp="3k37wixntajmy">
        {switchPhase === 'confirm' && switchTarget && <IosManualModal title={`Forward: ${switchTarget.name}`} ussdCode={`**67*${VEXU_NUMBER.replace(/[^0-9+]/g, '')}#`} phoneNumber={VEXU_NUMBER} onConfirm={confirmSwitch} onCancel={() => {
        setSwitchPhase(null);
        setSwitchTarget(null);
      }} data-id="element-267" data-mp="991smhzxvc03" />}
      </AnimatePresence>

      <AnimatePresence data-id="element-268" data-mp="3mqj9sojygdef">
        {(switchPhase === 'activating' || switchPhase === 'done') && <ActivationOverlay data-id="element-269" data-mp="3hrzis9qi8bf7" />}
      </AnimatePresence>

      <AnimatePresence data-id="element-270" data-mp="aoquptpnmfs">
        {disablePhase === 'confirm' && disableTarget && <IosManualModal title={`Disable: ${disableTarget.name}`} ussdCode={`##67#`} phoneNumber="" onConfirm={confirmDisable} onCancel={() => {
        setDisablePhase(null);
        setDisableTarget(null);
      }} data-id="element-271" data-mp="3roujmza92nwn" />}
      </AnimatePresence>

      <AnimatePresence data-id="element-272" data-mp="1eh398ccvfn0b">
        {(disablePhase === 'disabling' || disablePhase === 'done') && <DisableOverlay data-id="element-273" data-mp="1j9wd6y0imrbc" />}
      </AnimatePresence>
    </>;
}